import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

import { N0DES_API_PREFIX } from "@/api/domain"
import { Tag } from "@/api/tags"
import { connectWs } from "@/api/ws"
import type { ProjectIdParam } from "@/types"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Ticket {
  id: string
  project_id: string
  endpoint_id: string
  name: string
  ticket_kind: string
  live: boolean
  created_at: string
  updated_at: string
}

export interface TicketDetail extends Ticket {
  ticket_bytes: string // base64-encoded
  ticket_size: number
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export const ticketsApi = createApi({
  reducerPath: "ticketsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${N0DES_API_PREFIX}tickets`,
    mode: "cors",
    credentials: "include",
  }),
  tagTypes: [Tag.Tickets, Tag.TicketCount],
  endpoints: (build) => ({
    ticketCount: build.query<number, ProjectIdParam>({
      query: ({ project_id }) => `${project_id}/count`,
      providesTags: [Tag.TicketCount],
    }),

    ticketGet: build.query<
      TicketDetail,
      ProjectIdParam & { name: string; ticket_kind: string }
    >({
      query: ({ project_id, name, ticket_kind }) =>
        `${project_id}/${encodeURIComponent(name)}/${encodeURIComponent(ticket_kind)}`,
      providesTags: [Tag.Tickets],
    }),

    ticketDelete: build.mutation<
      void,
      ProjectIdParam & { name: string; ticket_kind: string }
    >({
      query: ({ project_id, name, ticket_kind }) => ({
        method: "DELETE",
        url: `${project_id}/${encodeURIComponent(name)}/${encodeURIComponent(ticket_kind)}`,
      }),
      invalidatesTags: [Tag.Tickets, Tag.TicketCount],
    }),

    ticketList: build.query<
      Ticket[],
      ProjectIdParam & { offset: number; limit: number }
    >({
      query: ({ project_id, offset, limit }) => ({
        url: project_id,
        params: { offset, limit },
      }),
      providesTags: [Tag.Tickets],

      async onCacheEntryAdded(
        args,
        { cacheDataLoaded, cacheEntryRemoved, dispatch },
      ) {
        await cacheDataLoaded

        let debounceTimer: ReturnType<typeof setTimeout> | null = null

        const cleanup = connectWs(
          `${N0DES_API_PREFIX}tickets/${args.project_id}/ws`,
          () => {
            // Debounce: only invalidate once per 500ms to avoid excessive refetches
            if (debounceTimer) clearTimeout(debounceTimer)
            debounceTimer = setTimeout(() => {
              dispatch(
                ticketsApi.util.invalidateTags([Tag.Tickets, Tag.TicketCount]),
              )
            }, 500)
          },
        )
        await cacheEntryRemoved
        if (debounceTimer) clearTimeout(debounceTimer)
        cleanup()
      },
    }),
  }),
})

export const {
  useTicketCountQuery,
  useTicketDeleteMutation,
  useTicketGetQuery,
  useTicketListQuery,
} = ticketsApi
